import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Button } from "src/components/ui/Button";
import LoadingComponent from "src/components/ui/LoadingComponent";
import { applicationStore } from "src/store/ApplicationStore";
import PaymentOverview from "./PaymentOverview";
import StripePaymentForm from "./StripePaymentForm";
import {
  createWorkflowSnapshot,
  initializePayment,
  normalizePaymentSession,
  syncWorkflowAfterPayment,
} from "./paymentService";

const EMPTY_PAYMENT_SETTINGS = {};

const getStripePublishableKey = () => {
  const runtimeEnv = (typeof window !== "undefined" && window.__ENV__) || {};
  return (
    runtimeEnv.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
    process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY ||
    ""
  ).trim();
};

const getRequestErrorMessage = (error, fallback) => {
  const detail =
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.response?.data?.error;
  if (typeof detail === "string" && detail.trim()) {
    return detail;
  }
  if (detail && typeof detail === "object") {
    return detail.message || JSON.stringify(detail);
  }
  return error?.message || fallback;
};

const PaymentVerification = observer(() => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const activeRef = useRef(true);
  const returnedPaymentHandledRef = useRef(false);
  const paymentResultHandledRef = useRef(false);
  const [session, setSession] = useState(null);
  const [phase, setPhase] = useState("loading");
  const [errorMessage, setErrorMessage] = useState("");
  const workflowResponse = applicationStore.workflowNextServiceResponse;
  const nodeId = workflowResponse?.node_id;
  const paymentSettings =
    workflowResponse?.payment ||
    workflowResponse?.node_settings ||
    workflowResponse?.node_settings_for_player ||
    EMPTY_PAYMENT_SETTINGS;
  const displayAmount = workflowResponse?.amount ?? paymentSettings.amount;
  const displayCurrency = workflowResponse?.currency ?? paymentSettings.currency;
  const returnParams = new URLSearchParams(window.location.search);
  const returnedPaymentIntentId = returnParams.get("payment_intent");
  const returnedPaymentStatus = returnParams.get("redirect_status");

  useEffect(
    () => () => {
      activeRef.current = false;
    },
    []
  );

  const loadPaymentSession = useCallback(
    async (retry = false) => {
      setPhase("loading");
      setErrorMessage("");

      try {
        const response = await initializePayment({
          accessToken: applicationStore.access_token,
          nodeId,
          retry,
          workflowId: workflowResponse?.workflow_id ?? applicationStore.workflowId,
          step: workflowResponse?.step,
        });
        const paymentSession = normalizePaymentSession(
          response,
          getStripePublishableKey()
        );

        if (activeRef.current) {
          setSession({
            ...paymentSession,
            amount: displayAmount,
            currency: displayCurrency,
          });
          setPhase("ready");
        }
      } catch (error) {
        if (activeRef.current) {
          const fallback =
            error?.message === "Payment node id is invalid."
              ? t("paymentVerification.invalidNode")
              : t("paymentVerification.sessionError");
          setErrorMessage(getRequestErrorMessage(error, fallback));
          setPhase("error");
        }
      }
    },
    [
      displayAmount,
      displayCurrency,
      nodeId,
      t,
      workflowResponse?.step,
      workflowResponse?.workflow_id,
    ]
  );

  useEffect(() => {
    activeRef.current = true;
    if (!returnedPaymentIntentId) {
      loadPaymentSession();
    }
  }, [loadPaymentSession, returnedPaymentIntentId]);

  const stripePromise = useMemo(
    () => (session?.publishableKey ? loadStripe(session.publishableKey) : null),
    [session?.publishableKey]
  );

  const advanceFlow = useCallback(
    (response) => {
      applicationStore.setWorkflowNextServiceResponse(response);
      navigate("/next", { replace: true });
    },
    [navigate]
  );

  const completePaymentStep = useCallback(
    async (paymentIntent) => {
      const stripeStatus = paymentIntent?.status || returnedPaymentStatus;

      const nextState = await syncWorkflowAfterPayment({
        accessToken: applicationStore.access_token,
        workflowSnapshot: createWorkflowSnapshot(workflowResponse),
        nodeId,
        workflowId: workflowResponse?.workflow_id ?? applicationStore.workflowId,
        step: workflowResponse?.step,
        stripeStatus,
        isActive: () => activeRef.current,
      });

      if (nextState) {
        advanceFlow(nextState);
      }
    },
    [
      advanceFlow,
      nodeId,
      returnedPaymentStatus,
      workflowResponse,
    ]
  );

  const handlePaymentResult = useCallback(
    async (paymentIntent) => {
      if (paymentResultHandledRef.current) {
        return;
      }
      paymentResultHandledRef.current = true;
      setPhase("confirming");
      setErrorMessage("");

      try {
        await completePaymentStep(paymentIntent);
      } catch (error) {
        paymentResultHandledRef.current = false;
        if (!activeRef.current) {
          return;
        }

        const fallback =
          error?.message === "Payment node id is invalid."
            ? t("paymentVerification.invalidNode")
            : t("paymentVerification.paymentFailed");
        setErrorMessage(getRequestErrorMessage(error, fallback));
        setPhase("error");
      }
    },
    [completePaymentStep, t]
  );

  useEffect(() => {
    if (!returnedPaymentIntentId || returnedPaymentHandledRef.current) {
      return;
    }
    returnedPaymentHandledRef.current = true;
    handlePaymentResult(null);
  }, [handlePaymentResult, returnedPaymentIntentId]);

  const handleRetry = () => {
    window.history.replaceState({}, document.title, window.location.pathname);
    setSession(null);
    paymentResultHandledRef.current = false;
    loadPaymentSession(true);
  };

  const instruction =
    workflowResponse?.node_settings_for_player?.instruction ||
    t("paymentVerification.description");
  const formattedAmount = useMemo(() => {
    if (session?.amount === undefined || session?.amount === null || !session?.currency) {
      return null;
    }
    try {
      return new Intl.NumberFormat(i18n.language, {
        style: "currency",
        currency: String(session.currency).toUpperCase(),
      }).format(Number(session.amount));
    } catch {
      return `${session.amount} ${String(session.currency).toUpperCase()}`;
    }
  }, [i18n.language, session?.amount, session?.currency]);

  if (phase === "loading") {
    return <LoadingComponent />;
  }

  const paymentOverview = (
    <PaymentOverview
      currency={session?.currency ?? displayCurrency}
      formattedAmount={formattedAmount}
      instruction={instruction}
      onBack={() =>
        navigate(`/?aid=${applicationStore.aidCode}`, { replace: true })
      }
      t={t}
    />
  );

  return (
    <div className="custom-container">
      <div className="flow-step-page text-left">
        {phase === "ready" && stripePromise && session?.clientSecret ? (
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret: session.clientSecret,
              appearance: {
                theme: "stripe",
                variables: {
                  borderRadius: "8px",
                  spacingUnit: "4px",
                },
                rules: {
                  ".Block": {
                    border: "none",
                    boxShadow: "none",
                    backgroundColor: "transparent",
                  },
                  ".Tab": {
                    border: "1px solid var(--flow-border, #e5e7eb)",
                    boxShadow: "none",
                  },
                  ".Tab--selected": {
                    border: "1px solid var(--flow-link, #635bff)",
                    boxShadow: "none",
                  },
                },
              },
            }}
          >
            <StripePaymentForm
              formattedAmount={formattedAmount}
              onPaymentResult={handlePaymentResult}
            >
              {paymentOverview}
            </StripePaymentForm>
          </Elements>
        ) : (
          <>
            <div className="flow-step-scroll w-full">
              {paymentOverview}

              {phase === "confirming" && (
                <div className="mt-10 text-center" role="status" aria-live="polite">
                  <LoadingComponent />
                  <p className="mt-4 text-body text-Gray900">
                    {t("paymentVerification.processing")}
                  </p>
                </div>
              )}

              {phase === "error" && (
                <div
                  className="mt-8 rounded-xl border border-red-200 bg-red-50/80 px-4 py-3"
                  role="alert"
                >
                  <p className="text-sm text-red-700">
                    {errorMessage || t("paymentVerification.paymentFailed")}
                  </p>
                </div>
              )}
            </div>

            {phase === "error" && (
              <div className="flow-step-action-footer">
                <Button variant="primaryWithoutIcon" onClick={handleRetry}>
                  {t("actions.retry")}
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
});

export default PaymentVerification;
